import axios from "axios";
import { logger } from "../util";
import { apiErrorList } from "../util/apiErrorMessage";
import Address from "./address";
import Influencer from "./influencer";
import CartProduct from "./cartProduct";
import CartTotals from "./cartTotals";

const baseEndpoint = "/api/cart";

const CART_FALLBACK = "Unable to update your cart. Please try again.";

/*
 * toServiceError converts an axios failure into an Error carrying the server's
 * user-facing message.
 *
 * Two response shapes are in play: errors.response() emits `{ message }` while
 * a few controllers emit `{ error }`. Read both, and fall back to a friendly
 * default so a raw network message never reaches the UI.
 */
const toServiceError = (err, fallback) => {
  const data = err?.isAxiosError ? err.response?.data : null;
  return new Error(data?.message || data?.error || fallback);
};

/*
 * cartRequest runs a cart HTTP call, returning its body or throwing a
 * user-facing Error. Shared so the error contract cannot drift between the
 * methods that use it.
 */
const cartRequest = async ({ send, fallback }) => {
  try {
    const { data } = await send();
    return data;
  } catch (err) {
    logger.error(err);
    throw toServiceError(err, fallback);
  }
};

export default class Cart {
  /*
   * addProduct adds a product to the user cart
   *
   * @param {object} initialization object
   *   {
   *     productId: number,
   *     personalization: {
   *       notes: string,
   *       personalizationTo: string,
   *       personalizationAddonCharacterName: string,
   *       personalizationAddonQuote: string,
   *     },
   *   }
   *
   * @returns {object} result
   */
  static async addProduct({
    productId,
    influencerId,
    personalization = {},
    addons = [],
    services = [],
  }) {
    try {
      const { data } = await axios.post(`${baseEndpoint}/${productId}`, {
        personalization,
        addons,
        influencerId,
        services,
      });

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * updateProduct updates a product in the user's cart
   *
   * @param {object} initialization object
   *   {
   *     cartProductId: number,
   *     personalization: {
   *       notes: string,
   *       personalizationTo: string,
   *       personalizationAddonCharacterName: string,
   *       personalizationAddonQuote: string,
   *     },
   *   }
   *
   * Throws on failure. Callers save personalization as part of a larger
   * sequence (service texts, checkout), so a silent failure here would let
   * them report success for changes that were never persisted.
   *
   * @returns {object} result
   */
  static updateProduct({ cartProductId, personalization }) {
    return cartRequest({
      send: () =>
        axios.put(`${baseEndpoint}/${cartProductId}`, { personalization }),
      fallback: "Unable to save your details. Please try again.",
    });
  }

  /*
   * removeProduct removes a product from the user cart
   *
   * @param {number} cartProductId
   * @returns {object} result
   */
  static async removeProduct(cartProductId) {
    try {
      const { data } = await axios.delete(`${baseEndpoint}/${cartProductId}`);

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * addProductAddon adds an addon to a cart product
   *
   * @param {object} initialization object { cartProductId: number, addonName: string }
   * @returns {object} result
   */
  static async addProductAddon({ cartProductId, addonName }) {
    try {
      const { data } = await axios.post(
        `${baseEndpoint}/${cartProductId}/${addonName}`,
      );

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * removeProductAddon removes an addon from a cart product
   *
   * @param {object} initialization object { cartProductId: number, addonName: string }
   * @returns {object} result
   */
  static async removeProductAddon({ cartProductId, addonName }) {
    try {
      const { data } = await axios.delete(
        `${baseEndpoint}/${cartProductId}/${addonName}`,
      );

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * clear clears a user cart
   *
   * @returns {object} result
   */
  static async clear() {
    try {
      const { data } = await axios.delete(baseEndpoint);

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * update updates a user cart with shop influencerId and trackingCode
   *
   * @param {object} updates object
   *   {
   *     influencerId: number,
   *     trackingCode: string,
   *     purchaseSubscription: boolean,
   *     didSetPurchaseSubscription: boolean,
   *     savePaymentInformation: boolean,
   *     pickupAtConvention: boolean,
   *     canContact: boolean
   *   }
   * @returns {object} result
   */
  static async update(updates) {
    try {
      const { data } = await axios.put(baseEndpoint, updates);

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * updateAddress updates a user's cart address
   *
   * @param {object} newAddress
   * @returns {object} result
   */
  static async updateAddress(newAddress = {}) {
    try {
      const { data } = await axios.put(`${baseEndpoint}/address`, {
        address: newAddress,
      });

      return data;
    } catch (err) {
      logger.error(err);

      return { errors: apiErrorList(err, CART_FALLBACK) };
    }
  }

  /*
   * Service calls throw on failure rather than returning the error body.
   *
   * The server surfaces validation failures (max quantity, exclusion group,
   * word limits) as 4xx responses. Swallowing them here meant callers had no
   * way to detect failure — `Promise.all` could never reject and the caller's
   * catch block was dead code, so a rejected service silently vanished.
   */
  static addService({ cartProductId, influencerServiceId, customerText }) {
    return cartRequest({
      send: () =>
        axios.post(`${baseEndpoint}/${cartProductId}/services`, {
          influencerServiceId,
          customerText,
        }),
      fallback: "Unable to add the service. Please try again.",
    });
  }

  static removeService({ cartProductId, cartProductServiceId }) {
    return cartRequest({
      send: () =>
        axios.delete(
          `${baseEndpoint}/${cartProductId}/services/${cartProductServiceId}`,
        ),
      fallback: "Unable to remove the service. Please try again.",
    });
  }

  static updateServiceText({
    cartProductId,
    cartProductServiceId,
    customerText,
  }) {
    return cartRequest({
      send: () =>
        axios.put(
          `${baseEndpoint}/${cartProductId}/services/${cartProductServiceId}`,
          { customerText },
        ),
      fallback: "Unable to save the service text. Please try again.",
    });
  }

  static from(rawCart) {
    return new Cart(rawCart);
  }

  #rawCart;
  #address;
  #cartProducts;
  #influencer;
  #totals;

  constructor(rawCart) {
    const rawCartCopy = { ...rawCart };
    const purchaseSubscription = Boolean(rawCartCopy.purchaseSubscription);

    this.#rawCart = rawCartCopy;
    this.#address = Address.from(rawCartCopy.address);
    this.#cartProducts = (rawCartCopy?.cartProducts ?? []).map((cartProduct) =>
      CartProduct.from(cartProduct, purchaseSubscription),
    );
    this.#influencer = Influencer.from(rawCartCopy.influencer);
    this.#totals = CartTotals.from(rawCartCopy.totals ?? {});
  }

  get address() {
    return this.#address;
  }

  get cartProducts() {
    return this.#cartProducts;
  }

  get token() {
    return this.#rawCart.cartToken;
  }

  get influencer() {
    return this.#influencer;
  }

  get influencerId() {
    return this.#rawCart.influencerId;
  }

  get totals() {
    return this.#totals;
  }

  get trackingCode() {
    return this.#rawCart.trackingCode;
  }

  get userId() {
    return this.#rawCart.userId;
  }

  get purchaseSubscription() {
    return Boolean(this.#rawCart.purchaseSubscription);
  }

  get didSetPurchaseSubscription() {
    return Boolean(this.#rawCart.didSetPurchaseSubscription);
  }

  get noShipmentChoice() {
    return Boolean(this.#rawCart.noShipmentChoice);
  }

  get shipmentGroups() {
    return this.#rawCart.shipmentGroups || [];
  }

  get shippingFeedback() {
    return this.#rawCart.shippingFeedback || "";
  }

  get savePaymentInformation() {
    return Boolean(this.#rawCart.savePaymentInformation);
  }

  get pickupAtConvention() {
    return Boolean(this.#rawCart.pickupAtConvention);
  }

  get canContact() {
    return Boolean(this.#rawCart.canContact);
  }

  get isEmpty() {
    return !Array.isArray(this.#cartProducts) || !this.#cartProducts.length;
  }

  get promoCode() {
    return this.#rawCart.promoCode;
  }

  mutate(key, value) {
    const rawCart = { ...this.#rawCart, [key]: value };
    return new Cart(rawCart);
  }

  mutateKeys(keyMap) {
    const rawCart = { ...this.#rawCart, ...keyMap };
    return new Cart(rawCart);
  }

  mutateKeysRaw(keyMap) {
    return { ...this.#rawCart, ...keyMap };
  }
}
